import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { WizardData } from "./OfferWizard";
import {
  getOfferOptionalItems,
  createOptionalItem,
  updateOptionalItem,
  deleteOptionalItem,
  getCategoryOptionsByType,
  HotelOfferOptionalItem,
  CreateOptionalItemData,
} from "../../services/api";
import CategoryDropdown from "./CategoryDropdown";
import DiscountOverridesSection from "./DiscountOverridesSection";

interface OptionsStepProps {
  data: WizardData;
  updateData: (data: Partial<WizardData>) => void;
  onNext: () => void;
  onPrev: () => void;
}

interface CategoryOption {
  id: string;
  name: string;
  description?: string;
  defaultUnitPrice: number;
  optionType: string;
  maxQuantity?: number;
}

type OptionType = "gastronomy" | "conference" | "additional";

// Draggable optional item row component
const DraggableOptionalItemRow: React.FC<{
  item: HotelOfferOptionalItem;
  editingItemId: string | null;
  editingItem: Partial<HotelOfferOptionalItem>;
  onStartEdit: (item: HotelOfferOptionalItem) => void;
  onSaveItem: (itemId: string) => void;
  onCancelEdit: () => void;
  onDeleteItem: (itemId: string) => void;
  setEditingItem: (item: Partial<HotelOfferOptionalItem>) => void;
  isLoading: boolean;
}> = ({
  item,
  editingItemId,
  editingItem,
  onStartEdit,
  onSaveItem,
  onCancelEdit,
  onDeleteItem,
  setEditingItem,
  isLoading,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("pl-PL", {
      style: "currency",
      currency: "PLN",
    }).format(price);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-3 p-4 border rounded-lg transition-shadow ${
        isDragging
          ? "shadow-lg border-blue-300 bg-blue-50"
          : "border-gray-200 hover:shadow-sm"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          <div
            {...attributes}
            {...listeners}
            className="mt-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
          >
            ⋮⋮
          </div>
          <div className="flex-1">
            {editingItemId === item.id ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nazwa
                  </label>
                  <input
                    type="text"
                    value={editingItem.title || ""}
                    onChange={(e) =>
                      setEditingItem({ ...editingItem, title: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    placeholder="Nazwa opcji..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cena jednostkowa
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingItem.unitPrice || ""}
                      onChange={(e) =>
                        setEditingItem({
                          ...editingItem,
                          unitPrice: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ilość
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={editingItem.quantity || ""}
                      onChange={(e) =>
                        setEditingItem({
                          ...editingItem,
                          quantity: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                    />
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => onSaveItem(item.id)}
                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                  >
                    Zapisz
                  </button>
                  <button
                    onClick={onCancelEdit}
                    className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
                  >
                    Anuluj
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="cursor-pointer hover:bg-gray-50 p-2 rounded"
                onClick={() => onStartEdit(item)}
              >
                <h4 className="font-medium text-gray-900">{item.title}</h4>
                <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                  <span>Cena: {formatPrice(item.unitPrice)}</span>
                  <span>Ilość: {item.quantity}</span>
                  <span className="font-semibold">
                    Razem: {formatPrice(item.totalPrice)}
                  </span>
                </div>
                {item.categoryOption && (
                  <p className="text-xs text-blue-600 mt-1">
                    Z kategorii: {item.categoryOption.title}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        {editingItemId !== item.id && (
          <button
            onClick={() => onDeleteItem(item.id)}
            disabled={isLoading}
            className="ml-3 px-2 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50"
          >
            Usuń
          </button>
        )}
      </div>
    </div>
  );
};

// Add new item form component
const AddOptionalItemForm: React.FC<{
  isVisible: boolean;
  categoryOptions: CategoryOption[];
  newItem: CreateOptionalItemData;
  setNewItem: (item: CreateOptionalItemData) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isLoading: boolean;
}> = ({
  isVisible,
  categoryOptions,
  newItem,
  setNewItem,
  onSubmit,
  onCancel,
  isLoading,
}) => {
  if (!isVisible) return null;

  const handleCategoryChange = (categoryOptionId: string) => {
    const selectedOption = categoryOptions.find(
      (opt) => opt.id === categoryOptionId
    );
    if (selectedOption) {
      setNewItem({
        ...newItem,
        categoryOptionId,
        title: selectedOption.name,
        unitPrice: selectedOption.defaultUnitPrice,
        quantity: 1,
      });
    } else {
      setNewItem({
        ...newItem,
        categoryOptionId: "",
      });
    }
  };

  return (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mb-4 bg-gray-50">
      <h4 className="font-medium text-gray-900 mb-3">Dodaj nową opcję</h4>

      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Wybierz z kategorii (opcjonalne)
          </label>
          <select
            value={newItem.categoryOptionId || ""}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded text-sm"
          >
            <option value="">-- Stwórz własną opcję --</option>
            {categoryOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name} -{" "}
                {new Intl.NumberFormat("pl-PL", {
                  style: "currency",
                  currency: "PLN",
                }).format(option.defaultUnitPrice)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nazwa opcji
          </label>
          <input
            type="text"
            value={newItem.title}
            onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
            className="w-full p-2 border border-gray-300 rounded text-sm"
            placeholder="np. DJ na wesele, Sala konferencyjna..."
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cena jednostkowa (PLN)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={newItem.unitPrice}
              onChange={(e) =>
                setNewItem({
                  ...newItem,
                  unitPrice: parseFloat(e.target.value) || 0,
                })
              }
              className="w-full p-2 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ilość
            </label>
            <input
              type="number"
              min="0"
              value={newItem.quantity}
              onChange={(e) =>
                setNewItem({
                  ...newItem,
                  quantity: parseInt(e.target.value) || 0,
                })
              }
              className="w-full p-2 border border-gray-300 rounded text-sm"
            />
          </div>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={onSubmit}
            disabled={isLoading || !newItem.title.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            Dodaj opcję
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
          >
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
};

const OptionsStep: React.FC<OptionsStepProps> = ({ data, onNext, onPrev }) => {
  // Determine if this is a multi-day stay (with overnight accommodation)
  const isMultiDayStay = useMemo(() => {
    if (!data.checkInDate || !data.checkOutDate) return false;
    const checkIn = new Date(data.checkInDate);
    const checkOut = new Date(data.checkOutDate);
    const nights = Math.ceil(
      (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
    );
    return nights >= 1;
  }, [data.checkInDate, data.checkOutDate]);

  const [isLoading, setIsLoading] = useState(false);
  const [optionalItems, setOptionalItems] = useState<HotelOfferOptionalItem[]>(
    []
  );
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<
    Partial<HotelOfferOptionalItem>
  >({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState<CreateOptionalItemData>({
    title: "",
    unitPrice: 0,
    quantity: 1,
  });
  const [error, setError] = useState<string | null>(null);

  // Category dropdown states
  const [activeDropdown, setActiveDropdown] = useState<OptionType | null>(null);
  const gastronomyButtonRef = useRef<HTMLButtonElement>(null);
  const conferenceButtonRef = useRef<HTMLButtonElement>(null);
  const additionalButtonRef = useRef<HTMLButtonElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadOptionalItems = useCallback(async () => {
    if (!data.offerId) return;

    setError(null);

    try {
      const items = await getOfferOptionalItems(data.offerId);
      setOptionalItems(items || []);
    } catch (err: any) {
      console.error("Error loading optional items:", err);
      setError(err.response?.data?.message || "Błąd podczas ładowania opcji");
    }
  }, [data.offerId]);

  const loadCategoryOptions = useCallback(
    async (type: OptionType = "additional") => {
      try {
        const options = await getCategoryOptionsByType(type);
        setCategoryOptions(options || []);
      } catch (err: any) {
        console.error("Error loading category options:", err);
      }
    },
    []
  );

  // Load data when component mounts
  useEffect(() => {
    if (data.offerId) {
      loadOptionalItems();
    }
    loadCategoryOptions();
  }, [data.offerId, loadOptionalItems, loadCategoryOptions]);

  const handleAddItem = async () => {
    if (!data.offerId || !newItem.title.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      await createOptionalItem(data.offerId, newItem);
      await loadOptionalItems();

      // Reset form
      setNewItem({
        title: "",
        unitPrice: 0,
        quantity: 1,
      });
      setShowAddForm(false);
    } catch (err: any) {
      console.error("Error adding optional item:", err);
      setError(err.response?.data?.message || "Błąd podczas dodawania opcji");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartEdit = (item: HotelOfferOptionalItem) => {
    setEditingItemId(item.id);
    setEditingItem({
      title: item.title,
      unitPrice: item.unitPrice,
      quantity: item.quantity,
    });
  };

  const handleSaveItem = async (itemId: string) => {
    if (!data.offerId) return;

    setError(null);

    try {
      await updateOptionalItem(data.offerId, itemId, {
        title: editingItem.title,
        unitPrice: Number(editingItem.unitPrice) || 0,
        quantity: Number(editingItem.quantity) || 0,
      });

      await loadOptionalItems();
      setEditingItemId(null);
      setEditingItem({});
    } catch (err: any) {
      console.error("Error updating item:", err);
      setError(err.response?.data?.message || "Błąd podczas zapisywania");
    }
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditingItem({});
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!data.offerId) return;

    setIsLoading(true);
    setError(null);

    try {
      await deleteOptionalItem(data.offerId, itemId);
      await loadOptionalItems();
    } catch (err: any) {
      console.error("Error deleting item:", err);
      setError(err.response?.data?.message || "Błąd podczas usuwania opcji");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || !data.offerId || active.id === over.id) return;

    const oldIndex = optionalItems.findIndex((item) => item.id === active.id);
    const newIndex = optionalItems.findIndex((item) => item.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newItems = arrayMove(optionalItems, oldIndex, newIndex);

      // Update local state immediately for better UX
      setOptionalItems(newItems);

      // Update sort orders in backend
      try {
        for (let i = 0; i < newItems.length; i++) {
          if (newItems[i].sortOrder !== i) {
            await updateOptionalItem(data.offerId, newItems[i].id, {
              sortOrder: i,
            });
          }
        }
      } catch (err: any) {
        console.error("Error reordering items:", err);
        setError(
          err.response?.data?.message || "Błąd podczas zmiany kolejności"
        );
        // Reload items to reset order if failed
        await loadOptionalItems();
      }
    }
  };

  const totalOptionalPrice = optionalItems.reduce(
    (sum, item) => sum + (Number(item.totalPrice) || 0),
    0
  );

  if (isLoading && optionalItems.length === 0) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
            <span className="text-gray-600">Ładowanie opcji...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold mb-6">Rabaty i Opcje</h2>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">
              {typeof error === "string"
                ? error
                : "Wystąpił błąd podczas ładowania opcji"}
            </p>
          </div>
        )}

        {/* Discount Overrides Section - only for multi-day stays */}
        {isMultiDayStay && data.offerId && (
          <DiscountOverridesSection offerId={data.offerId} />
        )}

        <p className="text-gray-600 mb-6">
          Dodaj opcjonalne usługi do oferty, takie jak DJ, sala konferencyjna,
          catering dodatkowy czy inne usługi, które nie wpływają na plan pobytu.
        </p>

        {/* Category buttons */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Wybierz kategorię opcji
          </h3>
          <div className="flex flex-wrap gap-3">
            <button
              ref={gastronomyButtonRef}
              onClick={() =>
                setActiveDropdown(
                  activeDropdown === "gastronomy" ? null : "gastronomy"
                )
              }
              className={`px-4 py-2 rounded-lg border transition-all duration-200 flex items-center space-x-2 ${
                activeDropdown === "gastronomy"
                  ? "bg-orange-100 border-orange-300 text-orange-800"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-orange-50 hover:border-orange-200"
              }`}
            >
              <span>🍽️</span>
              <span>Gastronomia</span>
              <span className="text-sm">▼</span>
            </button>

            <button
              ref={conferenceButtonRef}
              onClick={() =>
                setActiveDropdown(
                  activeDropdown === "conference" ? null : "conference"
                )
              }
              className={`px-4 py-2 rounded-lg border transition-all duration-200 flex items-center space-x-2 ${
                activeDropdown === "conference"
                  ? "bg-blue-100 border-blue-300 text-blue-800"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-200"
              }`}
            >
              <span>🏢</span>
              <span>Konferencje</span>
              <span className="text-sm">▼</span>
            </button>

            <button
              ref={additionalButtonRef}
              onClick={() =>
                setActiveDropdown(
                  activeDropdown === "additional" ? null : "additional"
                )
              }
              className={`px-4 py-2 rounded-lg border transition-all duration-200 flex items-center space-x-2 ${
                activeDropdown === "additional"
                  ? "bg-green-100 border-green-300 text-green-800"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-green-50 hover:border-green-200"
              }`}
            >
              <span>✨</span>
              <span>Dodatki</span>
              <span className="text-sm">▼</span>
            </button>
          </div>
        </div>

        {/* Add custom item button */}
        {!showAddForm && (
          <div className="mb-6">
            <button
              onClick={() => setShowAddForm(true)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center"
            >
              <span className="mr-2">+</span>
              Dodaj własną opcję
            </button>
          </div>
        )}

        {/* Add new item form */}
        <AddOptionalItemForm
          isVisible={showAddForm}
          categoryOptions={categoryOptions}
          newItem={newItem}
          setNewItem={setNewItem}
          onSubmit={handleAddItem}
          onCancel={() => {
            setShowAddForm(false);
            setNewItem({ title: "", unitPrice: 0, quantity: 1 });
          }}
          isLoading={isLoading}
        />

        {/* Optional Items List */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">
            Opcje w ofercie ({optionalItems.length})
          </h3>

          {optionalItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Nie dodano jeszcze żadnych opcji do oferty.</p>
              <p className="text-sm mt-2">
                Kliknij "Dodaj opcję" aby dodać pierwszą opcję.
              </p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={optionalItems.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <div>
                  {optionalItems.map((item) => (
                    <DraggableOptionalItemRow
                      key={item.id}
                      item={item}
                      editingItemId={editingItemId}
                      editingItem={editingItem}
                      onStartEdit={handleStartEdit}
                      onSaveItem={handleSaveItem}
                      onCancelEdit={handleCancelEdit}
                      onDeleteItem={handleDeleteItem}
                      setEditingItem={setEditingItem}
                      isLoading={isLoading}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Total summary */}
        {optionalItems.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="font-medium text-blue-900">
                Łączna wartość opcji:
              </span>
              <span className="text-xl font-bold text-blue-900">
                {new Intl.NumberFormat("pl-PL", {
                  style: "currency",
                  currency: "PLN",
                }).format(totalOptionalPrice)}
              </span>
            </div>
            <p className="text-sm text-blue-700 mt-1">
              Te opcje są wyświetlane oddzielnie od głównej oferty
            </p>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between">
          <button
            onClick={onPrev}
            disabled={isLoading}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Wstecz
          </button>
          <button
            onClick={onNext}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Ładowanie..." : "Dalej"}
          </button>
        </div>

        {/* Category Dropdowns */}
        {activeDropdown && (
          <CategoryDropdown
            categoryType={activeDropdown}
            isOpen={true}
            onClose={() => setActiveDropdown(null)}
            onAddAll={async (options) => {
              if (!data.offerId) return;
              setIsLoading(true);
              setError(null);

              try {
                for (const option of options) {
                  await createOptionalItem(data.offerId, {
                    title: option.name,
                    unitPrice: option.defaultUnitPrice,
                    quantity: 1,
                    categoryOptionId: option.id,
                  });
                }
                await loadOptionalItems();
              } catch (err: any) {
                console.error("Error adding options:", err);
                setError(
                  err.response?.data?.message || "Błąd podczas dodawania opcji"
                );
              } finally {
                setIsLoading(false);
              }
            }}
            onAddSingle={async (option) => {
              if (!data.offerId) return;
              setIsLoading(true);
              setError(null);

              try {
                await createOptionalItem(data.offerId, {
                  title: option.name,
                  unitPrice: option.defaultUnitPrice,
                  quantity: 1,
                  categoryOptionId: option.id,
                });
                await loadOptionalItems();
              } catch (err: any) {
                console.error("Error adding option:", err);
                setError(
                  err.response?.data?.message || "Błąd podczas dodawania opcji"
                );
              } finally {
                setIsLoading(false);
              }
            }}
            buttonRef={
              activeDropdown === "gastronomy"
                ? gastronomyButtonRef
                : activeDropdown === "conference"
                  ? conferenceButtonRef
                  : additionalButtonRef
            }
          />
        )}
      </div>
    </div>
  );
};

export default OptionsStep;
